import {delay, eventChannel, buffers} from 'redux-saga';
import {
  call,
  take,
  select,
  fork,
  put,
  actionChannel,
  spawn,
  takeEvery,
} from 'redux-saga/effects';
import _ from 'lodash';
import produce from 'immer';

// our old saga version doesn't have takeLeading
export const takeLeading = (patternOrChannel, saga, ...args) =>
  fork(function* takeLeadingInner() {
    while (true) {
      const action = yield take(patternOrChannel);
      yield call(saga, ...args.concat(action));
    }
  });

import {
  API_AUTH_LOGIN_SUCCESS,
  API_AUTH_REFRESH_FAILURE,
  LOGOUT,
  COLLABORATION_INIT_WITH_STATE_PROXY,
  COLLABORATION_ENTER_ROOM,
  COLLABORATION_ENTER_ROOM_FETCH_SUCCESS,
  COLLABORATION_LOCAL_DOCUMENT_UPDATE,
  COLLABORATION_REMOTE_DOCUMENT_UPDATE,
  COLLABORATION_CLOSE_ALL_ROOMS,
  CLEAR_DYNAMIC_COMPONENT,
  API_AUTH_REFRESH_SUCCESS,
  COLLABORATION_REFRESH_ROOM,
  COLLABORATION_SUBMISSION,
  COLLABORATION_SUBMISSION_LOCK,
  COLLABORATION_SUBMISSION_UNLOCK,
  COLLABORATION_LOCK_CHANGE,
  COLLABORATION_REFETCH_ROOM,
} from 'common/types';
import CollaborationActions from 'go/actions/api/collaboration';
import DynamicComponentActions from 'go/actions/dynamicComponent';
import {
  CollaborationWorker,
  CollaborationWorkerClass,
} from 'framework/collaboration/collaboration.office.js';
import {getRequestStorePath, isRoomManagedPath} from 'common/storeUtils';
import {LOCATION_CHANGE} from 'react-router-redux';
import {
  CW_STATUS_CHANGED,
  CW_LOCK_CHANGE,
  CW_SUBMISSION_LOCK,
  CW_SUBMISSION_UNLOCK,
} from 'framework/collaboration/types';

export const selectEntityId = (state) => _.get(state, 'dynamicComponent.data.entityId');
export const selectCsrftoken = (state) => _.get(state, 'auth.token._csrftoken');
export const selectStoreData = (action) => (state) =>
  _.get(state, `dynamicComponent.${getRequestStorePath(action)}`);
export const selectRoomConfig = (roomName) => (state) =>
  _.get(state, `dynamicComponent.collaboration.rooms.${roomName}`);
export const selectCollaboration = (state) =>
  _.get(state, 'dynamicComponent.collaboration');

// 10 minutes
export const REFRESH_THROTTLE = 10 * 60 * 1000;
const LOCAL_UPDATED_DEBOUNCE_WAIT = 1000;

export function* collaborationTokenRefresh() {
  try {
    /** @type {CollaborationWorkerClass} */
    const cw = yield call(initializeCollaborationWorker);
    let csrftoken = yield select(selectCsrftoken);
    yield call(() => cw.setToken(csrftoken));
  } catch (err) {
    console.error(`Unable to collaborationTokenRefresh ${err?.message}`);
  }
}

export function* collaborationDisconnect() {
  try {
    /** @type {CollaborationWorkerClass} */
    const cw = yield call(initializeCollaborationWorker);
    yield call(() => cw.leaveAllRooms());
    yield call(() => cw.setToken(''));
    yield call(() => cw.setLocalAwarenessState({}));
  } catch (err) {
    console.error(`Unable to collaborationDisconnect ${err?.message}`);
  }
}

export function* enterRoomFetchSuccess(action) {
  try {
    yield put(
      CollaborationActions.enterRoom({
        ..._.get(action, 'meta'),
        roomName: _.get(action, 'payload.entityId'), // inPayloadObj?
        status: _.get(action, 'payload.status'),
        storePath: `context.${_.get(action, 'meta.objectType')}`,
        isDependency: true,
      })
    );
  } catch (err) {
    console.error(`Unable to enterRoomFetchSuccess ${err?.message}`);
  }
}

export function* initWithStateProxy(action) {
  try {
    /** @type {CollaborationWorkerClass} */
    const cw = yield call(initializeCollaborationWorker);
    const state = yield select();
    const newAction = {
      ...action,
      roomName: _.get(action, 'initState.data.entityId'),
      status: _.get(action, 'initState.data.status'),
      storePath: 'data',
      collaborationStorePath: 'data',
      isDependency: false,
    };
    if (newAction.roomName && isRoomManagedPath(state, newAction)) {
      yield call(() => cw.leaveAllRooms());
      let csrftoken = yield select(selectCsrftoken);
      yield call(() => cw.setToken(csrftoken));
      yield put(CollaborationActions.enterRoom(newAction));
    } else {
      yield put(DynamicComponentActions.initWithState(action.initState));
    }
  } catch (err) {
    console.error(`Unable to initWithStateProxy ${err?.message}`);
  }
}

export function* enterRoom(action) {
  try {
    /** @type {CollaborationWorkerClass} */
    const cw = yield call(initializeCollaborationWorker);
    // caller must ensure it is not entering
    // the same room twice without leaving first
    yield cw.enterRoom(action.roomName, action.status);
  } catch (err) {
    console.error('collaboration-worker error enterRoom', err?.message);
  }
}

export function* remoteUpdate(cwAction) {
  try {
    const roomName = cwAction.roomName;
    const {storePath, initState} = yield select(selectRoomConfig(roomName));
    if (cwAction.type === CW_LOCK_CHANGE) {
      const putObj = {cwAction, type: COLLABORATION_LOCK_CHANGE};
      yield put(putObj);
      return;
    }

    if (initState) {
      const initCopy = produce(initState, (draft) => {
        _.set(draft, storePath, cwAction.payload); // inPayloadObj?
      });
      // we only want to initWithState *once*
      // so the reducer here will clear out the initState selected
      yield put(DynamicComponentActions.initWithState(initCopy));
    } else {
      yield put({
        cwAction,
        type: COLLABORATION_REMOTE_DOCUMENT_UPDATE,
      });
      yield put(DynamicComponentActions.validateConfigRules());
    }
  } catch (err) {
    console.log('collaboration-worker error handling remote update');
  }
}

export function* localUpdate(action) {
  try {
    /** @type {CollaborationWorkerClass} */
    const cw = yield call(initializeCollaborationWorker);
    const newStoreData = yield select(selectStoreData(action));
    yield cw.localUpdated(newStoreData.entityId, newStoreData);
  } catch (err) {
    console.error(`Unable to localUpdate ${err?.message}`);
  }
}

export function createRemoteUpdatedChannel(cw) {
  return eventChannel((emitter) => {
    try {
      cw.setRemoteCallback(emitter).catch((e) =>
        console.error(
          `app-collaboration ERROR unable to set remote callback ${e?.message}`
        )
      );
    } catch (err) {
      console.error(
        `app-collaboration ERROR unable to set remote callback ${err?.message}`
      );
    }
    // unsubscribe
    return () => {};
  }, buffers.expanding());
}

export function* remoteUpdateSetup() {
  try {
    /** @type {CollaborationWorkerClass} */
    const cw = yield call(initializeCollaborationWorker);
    const remoteUpdateChannel = yield call(createRemoteUpdatedChannel, cw);
    yield takeLeading(remoteUpdateChannel, remoteUpdate);
  } catch (err) {
    console.error(`unable to setup remote update call ${err.message}`);
  }
}

export function* leaveRoomsIfLocationChanges() {
  try {
    const collaborationState = yield select(selectCollaboration);
    let needToLeaveRooms = false;
    let currentPath = _.get(collaborationState, 'currentPathName');
    _.forEach(_.get(collaborationState, 'rooms'), (roomConfig, roomName) => {
      if (_.get(roomConfig, 'enteredAtPathName') !== currentPath) {
        needToLeaveRooms = true;
      }
    });
    if (needToLeaveRooms) {
      yield put(CollaborationActions.closeAllRooms());
    }
  } catch (err) {
    console.error(`Unable to leaveRoomsIfLocationChanges: ${err.message}`);
  }
}

export function* refreshRoom() {
  try {
    const entityId = yield select(selectEntityId);
    /** @type {CollaborationWorkerClass} */
    const cw = yield call(initializeCollaborationWorker);
    yield call(() => cw.sendStateless(entityId, 'CW_REFRESH_ROOM'));
  } catch (err) {
    console.error(`Unable to refreshRoom`, err);
  }
}

export function* refetchRoom(action) {
  try {
    const entityId = yield select(selectEntityId);
    const roomName = action.roomName || entityId;
    /** @type {CollaborationWorkerClass} */
    const cw = yield call(initializeCollaborationWorker);
    //TODO: update following with a 'CW_REFETCH_ROOM' once that is supported on backend
    yield call(() => cw.sendStateless(roomName, 'CW_REFETCH_ROOM'));
  } catch (err) {
    console.error(`Unable to refetchRoom`, err);
  }
}

export function* collaborationSubmission(action) {
  try {
    /** @type {CollaborationWorkerClass} */
    const cw = yield call(initializeCollaborationWorker);
    const {grantId, subGrantId, who, entityId, entityType} = action;
    const submissionData = JSON.stringify({
      submission: true,
      entityType,
      grantId,
      subGrantId,
      who,
    });
    const payloadPrefix =
      action.type === COLLABORATION_SUBMISSION_LOCK
        ? CW_SUBMISSION_LOCK
        : CW_STATUS_CHANGED;
    const payload = `${payloadPrefix}/${submissionData}`;
    yield call(() => cw.sendStateless(entityId, payload));
  } catch (err) {
    console.error(`Unable to collaborationSubmission ${err?.message}`);
  }
}

export function* collaborationSubmissionUnlock(action) {
  try {
    /** @type {CollaborationWorkerClass} */
    const cw = yield call(initializeCollaborationWorker);
    const {entityId} = action;
    const payload = `${CW_SUBMISSION_UNLOCK}`;
    yield call(() => cw.sendStateless(entityId, payload));
  } catch (err) {
    console.error(`Unable to collaborationSubmissionUnlock ${err?.message}`);
  }
}

// use this for testing to mock the collaboration worker
let collaborationWorkerPromise;

export const initializeCollaborationWorker = () => {
  if (!collaborationWorkerPromise) {
    collaborationWorkerPromise = CollaborationWorker();
  }
  return collaborationWorkerPromise;
};

let hasInit = false;
export function* collaborationSetupInternal() {
  // no-re-entry, error handling is inline.
  if (hasInit) {
    return;
  }
  hasInit = true;
  try {
    // take leading, only take one at a time, and only when ready
    yield takeLeading(
      [API_AUTH_LOGIN_SUCCESS, API_AUTH_REFRESH_SUCCESS],
      collaborationTokenRefresh
    );
    // take every, it's ok to fork and do multiple at once
    yield takeEvery(
      [
        LOGOUT,
        API_AUTH_REFRESH_FAILURE,
        COLLABORATION_CLOSE_ALL_ROOMS,
        CLEAR_DYNAMIC_COMPONENT,
      ],
      collaborationDisconnect
    );
    yield takeEvery(COLLABORATION_ENTER_ROOM, enterRoom);
    yield takeEvery(COLLABORATION_INIT_WITH_STATE_PROXY, initWithStateProxy);
    yield takeEvery(COLLABORATION_ENTER_ROOM_FETCH_SUCCESS, enterRoomFetchSuccess);
    yield takeEvery(LOCATION_CHANGE, leaveRoomsIfLocationChanges);
    yield takeEvery(COLLABORATION_REFRESH_ROOM, refreshRoom);
    yield takeEvery(COLLABORATION_REFETCH_ROOM, refetchRoom);
    yield takeEvery(
      [COLLABORATION_SUBMISSION, COLLABORATION_SUBMISSION_LOCK],
      collaborationSubmission
    );
    yield takeEvery(COLLABORATION_SUBMISSION_UNLOCK, collaborationSubmissionUnlock);
    // action channel, one at a time and don't lose any (todo: even on router change? won't it get cleared then we put the deleted data? that would be bad....)
    let localUpdateChannel = yield actionChannel(
      COLLABORATION_LOCAL_DOCUMENT_UPDATE,
      buffers.expanding()
    );
    yield takeLeading(localUpdateChannel, localUpdate);
    // remoteSetup: queued one at a time event emitter from the callback
    yield spawn(remoteUpdateSetup);
  } catch (err) {
    console.error(`collaboration-worker: Unable to init ${err.message}`);
  }
}

export default function* collaborationSetupSaga() {
  yield spawn(collaborationSetupInternal);
}
